/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ILinkStyleProps, ILinkStyles } from '@fluentui/react/lib/link';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';

const messages = defineMessages({
    speakerSeparation: { id: 'languageStudio.callCenterDemoPage.speakerSeparation', defaultMessage: 'Speaker separation' },
    conversationPii: { id: 'languageStudio.callCenterDemoPage.conversationPii', defaultMessage: 'Conversation PII (Preview)' },
    batchTranscription: { id: 'languageStudio.callCenterDemoPage.batchTranscription', defaultMessage: 'Batch transcription API' },
    speechTechnologies: { id: 'languageStudio.callCenterDemoPage.speechTechnologies', defaultMessage: 'Speech technologies used' },
    summarization: { id: 'languageStudio.callCenterDemoPage.summarization', defaultMessage: 'Conversation summarization (Preview)' },
    languageTechnologies: { id: 'languageStudio.callCenterDemoPage.languageTechnologies', defaultMessage: 'Language technologies used' },
    sentimentAnalysis: {
        id: 'languageStudio.callCenterDemoPage.sentimentAnalysis',
        defaultMessage: 'Conversation sentiment analysis (Preview)'
    }
});

const headerTextStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({ root: { whiteSpace: 'nowrap' } });
const linkTextStyles = classNamesFunction<ILinkStyleProps, ILinkStyles>()({ root: { whiteSpace: 'nowrap', fontSize: '14px' } });

export const CallCenterTechnologies = () => {
    const { localize } = useLocalization();

    return (
        <Stack tokens={{ childrenGap: 10 }}>
            <Stack tokens={{ childrenGap: 2 }}>
                <LuisText styles={headerTextStyles} as="h2" fontWeight={500}>
                    {localize(messages.speechTechnologies)}
                </LuisText>
                <ExternalLink
                    styles={linkTextStyles}
                    href="https://docs.microsoft.com/azure/cognitive-services/speech-service/batch-transcription">
                    {localize(messages.batchTranscription)}
                </ExternalLink>
                <ExternalLink
                    styles={linkTextStyles}
                    href="https://docs.microsoft.com/azure/cognitive-services/speech-service/batch-transcription#speaker-separation-diarization">
                    {localize(messages.speakerSeparation)}
                </ExternalLink>
            </Stack>

            <Stack tokens={{ childrenGap: 2 }}>
                <LuisText styles={headerTextStyles} as="h2" fontWeight={500}>
                    {localize(messages.languageTechnologies)}
                </LuisText>
                <ExternalLink
                    styles={linkTextStyles}
                    href="https://docs.microsoft.com/azure/cognitive-services/language-service/personally-identifiable-information/how-to-call-for-conversations?tabs=client-libraries">
                    {localize(messages.conversationPii)}
                </ExternalLink>
                <ExternalLink styles={linkTextStyles} href="https://aka.ms/sentiment-conversation">
                    {localize(messages.sentimentAnalysis)}
                </ExternalLink>
                <ExternalLink
                    styles={linkTextStyles}
                    href="https://docs.microsoft.com/azure/cognitive-services/language-service/summarization/overview?tabs=document-summarization">
                    {localize(messages.summarization)}
                </ExternalLink>
            </Stack>
        </Stack>
    );
};
